declare global {
  interface Window {
    fireGWTEvent: (event: string) => void;
  }
}

import { defineStore } from 'pinia';
import { ref } from 'vue';
import { AuthorizationApi } from '@/api/javaapi';
import { useCookies } from 'vue3-cookies';
import { useRouter } from 'vue-router';
import { gwtBridge } from '@/GWTBridge';

export const useAuthStore = defineStore('auth', () => {
  const { cookies } = useCookies();
  const token = ref<string | null>(cookies.get('bssid'));
  const permissions = ref<Record<string, string[]>>({});
  const returnUrl = ref<string | null>(null);
  const router = useRouter();
  const authApi = new AuthorizationApi();

  async function login(username: string, password: string) {
    try {
      const response = await authApi.login({ username, password });
      token.value = response.data.token;
      permissions.value = response.data.permissions;
      gwtBridge.then((bridge) => {
        bridge.fireEvent('Login');
      });
      if (returnUrl.value) {
        router.push(returnUrl.value);
        returnUrl.value = null;
      } else {
        router.push('/rm4/Login.php');
      }
    } catch (error: any) {
      token.value = null;
      permissions.value = {};
      throw new Error(error.response?.data || 'Login failed');
    }
  }

  async function logout() {
    try {
      if (token.value) {
        await authApi.logout(token.value);
      }
    } catch (error) {}
    token.value = null;
    permissions.value = {};
    gwtBridge.then((bridge) => {
      bridge.fireEvent('Logout');
    });
    router.push('/login');
  }

  async function validateToken() {
    if (!token.value) return false;
    try {
      const response = await authApi.validateToken(token.value);
      token.value = response.data.token;
      permissions.value = response.data.permissions;
      return true;
    } catch (error: any) {
      if (error.response?.status === 401) {
        await logout();
      }
      return false;
    }
  }

  function hasPermission(screen: string, action: string): boolean {
    const actions = permissions.value[screen];
    return actions ? actions.includes(action) : false;
  }

  if (token.value) {
    validateToken();
  }

  return { token, returnUrl, login, logout, validateToken, hasPermission };
});